import React, { useState, useRef } from 'react';

export default function ImportPaymentsModal({ students, onImport, onClose }) {
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState([]);
  const [errors, setErrors] = useState([]);
  const [step, setStep] = useState('input'); // input | preview | done
  const [importCount, setImportCount] = useState(0);
  const fileRef = useRef();

  const studentNames = new Set(students.map(s => s.name));

  function parseCSV(text) {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return;

    // 自動偵測分隔符號（逗號或 Tab）
    const sep = lines[0].includes('\t') ? '\t' : ',';

    // 跳過標題行（如果第一行包含「日期」或「學員」）
    const firstLine = lines[0];
    const hasHeader = /日期|學員|date|name/i.test(firstLine);
    const dataLines = hasHeader ? lines.slice(1) : lines;

    const results = [];
    const errs = [];

    dataLines.forEach((line, idx) => {
      const cols = line.split(sep).map(c => c.trim().replace(/^"|"$/g, ''));
      // 欄位：日期(0)、學員(1)、堂數(2)、金額(3)、備註(4)
      const [date, name, lessons, amount, notes] = cols;

      if (!name || !date) {
        errs.push(`第 ${idx + 2} 行：缺少姓名或日期`);
        return;
      }

      // 日期格式統一補零
      const rawDate = date.replace(/\//g, '-').trim();
      const dateParts = rawDate.split('-');
      const normalizedDate = dateParts.length === 3
        ? `${dateParts[0]}-${String(dateParts[1]).padStart(2,'0')}-${String(dateParts[2]).padStart(2,'0')}`
        : rawDate;

      const lessonsNum = parseInt(lessons) || 0;
      const amountNum = parseInt(amount) || 0;

      if (lessonsNum <= 0) {
        errs.push(`第 ${idx + 2} 行（${name}）：堂數無效`);
        return;
      }

      // 找對應學員（精確比對）
      const matchedStudent = students.find(s => s.name === name);

      // ★ 重複偵測：同學員、同日期、同金額
      const isDuplicate = matchedStudent
        ? (matchedStudent.payments || []).some(p =>
            !p.isProxy &&
            p.date === normalizedDate &&
            Number(p.amount) === amountNum
          )
        : false;

      results.push({
        date: normalizedDate,
        name,
        lessons: lessonsNum,
        amount: amountNum,
        notes: notes || '',
        matched: !!matchedStudent,
        studentId: matchedStudent?.id || null,
        isDuplicate,
      });
    });

    setParsed(results);
    setErrors(errs);
    setStep('preview');
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setCsvText(ev.target.result);
      parseCSV(ev.target.result);
    };
    reader.readAsText(file, 'UTF-8');
  }

  function handlePaste(e) {
    const text = e.clipboardData.getData('text');
    setCsvText(text);
    parseCSV(text);
  }

  function doImport() {
    const toImport = parsed.filter(r => r.matched && !r.isDuplicate);
    onImport(toImport.map(r => ({
      studentId: r.studentId,
      payment: {
        date: r.date,
        lessons: r.lessons,
        amount: r.amount,
        notes: r.notes,
      }
    })));
    setImportCount(toImport.length);
    setStep('done');
  }

  const matchedCount = parsed.filter(r => r.matched && !r.isDuplicate).length;
  const unmatchedCount = parsed.filter(r => !r.matched).length;
  const duplicateCount = parsed.filter(r => r.isDuplicate).length;

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.header}>
          <span style={{ fontSize: 15, fontWeight: 500 }}>批次匯入付款紀錄</span>
          <button style={S.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={{ padding: '18px 20px', maxHeight: 520, overflowY: 'auto' }}>

          {step === 'input' && (
            <>
              <p style={{ fontSize: 13, color: '#555', marginBottom: 14, lineHeight: 1.7 }}>
                支援從 Excel 複製貼上，或上傳 CSV 檔案。<br/>
                欄位順序：<strong>日期、學員姓名、堂數、金額、備註</strong>（備註可省略）
              </p>

              {/* 上傳按鈕 */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <button style={S.uploadBtn} onClick={() => fileRef.current.click()}>
                  上傳 CSV 檔案
                </button>
                <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={handleFile} />
                <span style={{ fontSize: 12, color: '#aaa', alignSelf: 'center' }}>或直接在下方貼上資料</span>
              </div>

              {/* 貼上區域 */}
              <textarea
                style={S.textarea}
                placeholder={`貼上 Excel 複製的資料，例如：\n2025/06/24\t林群欽\t20\t24000\t付款 20 堂\n2025/08/27\t大康\t10\t14000\t付款 10 堂`}
                value={csvText}
                onChange={e => setCsvText(e.target.value)}
                onPaste={handlePaste}
              />

              <button
                style={{ ...S.btnP, marginTop: 10, opacity: csvText.trim() ? 1 : 0.5 }}
                onClick={() => parseCSV(csvText)}
                disabled={!csvText.trim()}
              >
                解析資料
              </button>
            </>
          )}

          {step === 'preview' && (
            <>
              {/* 統計摘要 */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 16 }}>
                <div style={S.statCard}>
                  <div style={S.statLabel}>共解析</div>
                  <div style={S.statValue}>{parsed.length} 筆</div>
                </div>
                <div style={{ ...S.statCard, background: matchedCount > 0 ? '#e6f4ea' : '#f5f5f5' }}>
                  <div style={S.statLabel}>可匯入</div>
                  <div style={{ ...S.statValue, color: '#188038' }}>{matchedCount} 筆</div>
                </div>
                <div style={{ ...S.statCard, background: duplicateCount > 0 ? '#fef9e7' : '#f5f5f5' }}>
                  <div style={S.statLabel}>重複跳過</div>
                  <div style={{ ...S.statValue, color: duplicateCount > 0 ? '#b46309' : '#aaa' }}>{duplicateCount} 筆</div>
                </div>
                <div style={{ ...S.statCard, background: unmatchedCount > 0 ? '#fce8e6' : '#f5f5f5' }}>
                  <div style={S.statLabel}>找不到學員</div>
                  <div style={{ ...S.statValue, color: unmatchedCount > 0 ? '#c5221f' : '#aaa' }}>{unmatchedCount} 筆</div>
                </div>
              </div>

              {errors.length > 0 && (
                <div style={{ background: '#fce8e6', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#c5221f' }}>
                  {errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}

              {/* 重複警告 */}
              {duplicateCount > 0 && (
                <div style={{ background: '#fef9e7', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#b46309' }}>
                  ⚠ 偵測到 {duplicateCount} 筆重複資料（同學員、同日期、同金額），匯入時將自動跳過。若資料有誤請按「← 重新貼上」修改。
                </div>
              )}

              {/* 預覽表格 */}
              <div style={{ border: '0.5px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8f8f8' }}>
                      <th style={S.th}>狀態</th>
                      <th style={S.th}>學員</th>
                      <th style={S.th}>日期</th>
                      <th style={S.th}>堂數</th>
                      <th style={S.th}>金額</th>
                      <th style={S.th}>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.map((r, i) => (
                      <tr key={i} style={{
                        background: r.isDuplicate ? '#fffbf0' : r.matched ? '#fff' : '#fff5f5'
                      }}>
                        <td style={{ ...S.td, textAlign: 'center' }}>
                          {r.isDuplicate
                            ? <span style={{ color: '#b46309', fontWeight: 500 }} title="重複資料，將跳過">⚠</span>
                            : r.matched
                              ? <span style={{ color: '#188038', fontWeight: 500 }}>✓</span>
                              : <span style={{ color: '#c5221f' }} title="找不到此學員">✗</span>
                          }
                        </td>
                        <td style={{ ...S.td, fontWeight: 500, color: r.isDuplicate ? '#b46309' : r.matched ? '#222' : '#e53935' }}>{r.name}</td>
                        <td style={{ ...S.td, color: r.isDuplicate ? '#b46309' : 'inherit' }}>{r.date}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{r.lessons}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>${r.amount.toLocaleString()}</td>
                        <td style={{ ...S.td, color: '#888' }}>{r.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {unmatchedCount > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#b46309', background: '#fef9e7', borderRadius: 8, padding: '8px 12px' }}>
                  ✗ 標記的學員名字在程式裡找不到，請確認名字是否一致（包含空格、全半形）
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button style={S.btnS} onClick={() => setStep('input')}>← 重新貼上</button>
              </div>
            </>
          )}

          {step === 'done' && (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 16, fontWeight: 500, color: '#188038', marginBottom: 8 }}>
                成功匯入 {importCount} 筆付款紀錄
              </div>
              <div style={{ fontSize: 13, color: '#888' }}>
                請至各學員的付款紀錄確認資料正確
              </div>
            </div>
          )}
        </div>

        <div style={S.footer}>
          <button style={S.btnS} onClick={onClose}>
            {step === 'done' ? '關閉' : '取消'}
          </button>
          {step === 'preview' && matchedCount > 0 && (
            <button style={S.btnP} onClick={doImport}>
              匯入 {matchedCount} 筆付款紀錄
            </button>
          )}        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, border: '0.5px solid #e0e0e0', width: 600, maxWidth: '97vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '0.5px solid #eee', flexShrink: 0 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '0.5px solid #eee', flexShrink: 0 },
  closeBtn: { background: 'none', border: 'none', fontSize: 20, color: '#888', cursor: 'pointer', lineHeight: 1 },
  textarea: { width: '100%', height: 160, border: '0.5px solid #ccc', borderRadius: 8, padding: '10px 12px', fontSize: 12, fontFamily: 'monospace', resize: 'vertical' },
  uploadBtn: { padding: '7px 16px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#444', cursor: 'pointer' },
  statCard: { background: '#f5f5f5', borderRadius: 8, padding: '10px 12px' },
  statLabel: { fontSize: 11, color: '#888', marginBottom: 3 },
  statValue: { fontSize: 20, fontWeight: 500, color: '#222' },
  th: { padding: '8px 10px', textAlign: 'left', fontWeight: 500, color: '#666', borderBottom: '0.5px solid #e0e0e0' },
  td: { padding: '7px 10px', borderBottom: '0.5px solid #f0f0f0', fontSize: 12 },
  btnS: { padding: '7px 16px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#666', cursor: 'pointer' },
  btnP: { padding: '7px 16px', borderRadius: 8, fontSize: 13, background: '#188038', border: 'none', color: '#fff', fontWeight: 500, cursor: 'pointer' },
};
